import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { LEXIQUE_TERMS } from "@/lib/lexique";
import { useSEO, BASE } from "@/lib/seo";
import ArticleShell, { H2, P, SeeAlso } from "@/components/site/ArticleShell";
import { RichText } from "@/components/site/RichText";
import { IlluFigure } from "@/components/site/IlluFigure";
import { LEXIQUE_ILLUS } from "@/lib/illustrations";

// Tableau comparatif commun aux fiches bas-relief / ronde-bosse
const RELIEF_TABLE = [
  {
    term: "Bas-relief",
    slug: "bas-relief",
    saillie: "Faible — moins de la moitié du volume réel",
    fond: "Attaché au fond",
    vue: "De face",
    exemple: "Frise du Parthénon, médailles, monnaies",
  },
  {
    term: "Haut-relief",
    slug: "haut-relief",
    saillie: "Forte — plus de la moitié, certaines parties presque détachées",
    fond: "Attaché au fond",
    vue: "De face, légèrement de profil",
    exemple: "Tympans des cathédrales gothiques",
  },
  {
    term: "Ronde-bosse",
    slug: "ronde-bosse",
    saillie: "Complète — volume entier",
    fond: "Détachée de tout fond",
    vue: "On en fait le tour",
    exemple: "Vénus de Milo, Le Penseur",
  },
];

const ReliefTable = ({ currentSlug }) => (
  <div className="mt-8 overflow-x-auto" data-testid="tableau-reliefs">
    <table className="w-full min-w-[640px] border border-ink/15 text-left">
      <thead>
        <tr className="border-b border-ink/15 font-mono text-[10px] uppercase tracking-[0.16em] text-bronze">
          <th className="p-4 font-medium">Famille</th>
          <th className="p-4 font-medium">Saillie</th>
          <th className="p-4 font-medium">Lien au fond</th>
          <th className="p-4 font-medium">Point de vue</th>
          <th className="p-4 font-medium">Exemple</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-ink/10">
        {RELIEF_TABLE.map((row) => (
          <tr key={row.slug}>
            <td className="p-4 font-serif text-base text-ink">
              {row.slug === currentSlug ? (
                row.term
              ) : (
                <Link
                  to={`/lexique-sculpture/${row.slug}`}
                  data-testid={`tableau-lien-${row.slug}`}
                  className="text-bronze transition-colors hover:text-ink"
                >
                  {row.term}
                </Link>
              )}
            </td>
            <td className="p-4 text-sm font-light leading-relaxed text-body">{row.saillie}</td>
            <td className="p-4 text-sm font-light leading-relaxed text-body">{row.fond}</td>
            <td className="p-4 text-sm font-light leading-relaxed text-body">{row.vue}</td>
            <td className="p-4 text-sm font-light leading-relaxed text-body">{row.exemple}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function TermPage() {
  const { slug } = useParams();
  const t = LEXIQUE_TERMS.find((x) => x.slug === slug);

  const definedTerm = t
    ? {
        "@type": "DefinedTerm",
        name: t.term,
        description: t.def,
        inLanguage: "fr-FR",
        inDefinedTermSet: {
          "@type": "DefinedTermSet",
          name: "Lexique de la sculpture contemporaine",
          url: `${BASE}/lexique-sculpture`,
        },
      }
    : null;

  const jsonLd = t
    ? t.rich
      ? {
          "@context": "https://schema.org",
          "@graph": [
            definedTerm,
            {
              "@type": "FAQPage",
              mainEntity: t.rich.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }
      : { "@context": "https://schema.org", ...definedTerm }
    : null;

  useSEO(
    t ? t.seoTitle : "Lexique de la sculpture — Sculpteur Français",
    t ? t.seoDescription : "Lexique de la sculpture contemporaine : les termes du domaine expliqués simplement.",
    jsonLd,
    {
      breadcrumb: [
        { name: "Lexique de la sculpture", path: "/lexique-sculpture" },
        { name: t ? t.term : "", path: `/lexique-sculpture/${slug}` },
      ],
    }
  );

  if (!t) return <Navigate to="/lexique-sculpture" replace />;

  const sections = [
    ["D'où vient ce terme ?", t.origin],
    ["Concrètement, en sculpture contemporaine", t.concrete],
    ["Comment le repérer ?", t.spot],
  ];

  return (
    <ArticleShell
      testId={`terme-${slug}-page`}
      eyebrow={`Lexique — ${t.cat}`}
      titleLines={[t.h1 || t.term]}
      lead={t.def}
    >
      {t.rich ? (
        <>
          <div className="border border-ink/15 p-6 sm:p-8" data-testid="terme-definition-box">
            <p className="eyebrow">Définition</p>
            <p className="mt-4 font-serif text-xl font-normal leading-relaxed text-ink sm:text-2xl">
              {t.rich.definition}
            </p>
          </div>

          {t.rich.image && (
            <figure className="mt-10" data-testid="terme-illustration">
              <img
                src={t.rich.image.src}
                alt={t.rich.image.alt}
                width={t.rich.image.width}
                height={t.rich.image.height}
                loading="lazy"
                className="img-content border border-ink/15"
              />
              <figcaption className="mt-3 font-mono text-[11px] font-light leading-relaxed tracking-wide text-smoke">
                {t.rich.image.caption}{" "}
                <a
                  href={t.rich.image.creditUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="terme-illustration-credit"
                  className="text-bronze underline-offset-4 transition-colors hover:underline"
                >
                  {t.rich.image.credit}
                </a>
              </figcaption>
            </figure>
          )}

          <Link
            to="/lexique-sculpture"
            data-testid="terme-back-link"
            className="group mt-10 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-bronze"
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-1" />
            <span className="link-underline">Tout le lexique</span>
          </Link>

          {t.rich.sections.map((s) => (
            <div key={s.h2}>
              <H2>{s.h2}</H2>
              {s.paragraphs.map((p, i) => (
                <P key={i}>{p}</P>
              ))}
              {s.table && <ReliefTable currentSlug={slug} />}
            </div>
          ))}

          <H2>{t.rich.faqTitle}</H2>
          <div className="divide-y divide-ink/10 border-y border-ink/10" data-testid="terme-faq">
            {t.rich.faq.map((f) => (
              <div key={f.q} className="py-6">
                <p className="font-serif text-xl text-ink">{f.q}</p>
                <p className="mt-3 text-base font-light leading-relaxed text-body">
                  <RichText text={f.a} />
                </p>
                {f.link && (
                  <Link
                    to={f.link.to}
                    data-testid={`faq-lien-${f.link.to.split("/").pop()}`}
                    className="group mt-3 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-bronze"
                  >
                    <span className="link-underline">{f.link.label}</span>
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                )}
              </div>
            ))}
          </div>

          <P>{t.rich.conclusion}</P>
        </>
      ) : (
        <>
          <Link
            to="/lexique-sculpture"
            data-testid="terme-back-link"
            className="group mb-2 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-bronze"
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-1" />
            <span className="link-underline">Tout le lexique</span>
          </Link>

          {(LEXIQUE_ILLUS[slug] || []).map((entry, i) => (
            <IlluFigure
              key={entry.src}
              entry={entry}
              testId={`terme-illustration-${i}`}
              className={i === 0 ? "" : "mt-10"}
            />
          ))}

          {sections.map(([h2, text]) => (
            <div key={h2}>
              <H2>{h2}</H2>
              <P>{text}</P>
              {t.exampleLink && h2 === "Concrètement, en sculpture contemporaine" && (
                <p className="mt-5 text-base font-light leading-relaxed text-body sm:text-lg" data-testid="terme-exemple-lien">
                  {t.exampleLink.text}
                  <a
                    href={t.exampleLink.url}
                    target="_blank"
                    rel="noopener"
                    className="text-bronze underline decoration-bronze/40 underline-offset-4 transition-colors hover:decoration-bronze"
                  >
                    {t.exampleLink.label}
                  </a>
                  {t.exampleLink.after}
                </p>
              )}
            </div>
          ))}
        </>
      )}

      <SeeAlso
        links={[
          ...t.links,
          { to: "/lexique-sculpture", label: "Tous les termes du lexique" },
        ]}
      />
    </ArticleShell>
  );
}
